import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Avatar, message, Modal, Button, Space, Popconfirm, Spin } from "antd";
import { UserOutlined, CameraOutlined, DeleteOutlined } from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import apiPath from "../../constants/apiPath";

const MAX_SIZE_BYTES = 3 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Click the topbar avatar to open a WhatsApp-style popup: the picture shown
// large, with Change/Remove actions inside the popup itself rather than
// cramming tiny overlay badges onto a 32px avatar. Fetches the current
// picture as an authenticated blob (the S3 bucket is private, so a plain
// <img src> can't hit it directly — same reasoning as the existing bill/
// document download buttons elsewhere in the app).
const ProfileAvatar = () => {
  const { userProfile, setUserProfile } = useContext(AuthContext);
  const [pictureUrl, setPictureUrl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let objectUrl;
    const token = localStorage.getItem("i_ems_admin_token") || "";
    if (userProfile?.profilePic) {
      axios
        .get(`${apiPath.baseURL}${apiPath.profilePicture}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        })
        .then((res) => {
          objectUrl = window.URL.createObjectURL(res.data);
          setPictureUrl(objectUrl);
        })
        .catch(() => {
          /* no picture yet or fetch failed — falls back to the icon avatar */
        });
    } else {
      setPictureUrl(null);
    }
    return () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.profilePic]);

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error("Only JPEG, PNG, or WEBP images are allowed");
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      message.error("Image must be 3MB or smaller");
      return;
    }

    const previousUrl = pictureUrl;
    setPictureUrl(window.URL.createObjectURL(file));
    setUploading(true);

    try {
      const token = localStorage.getItem("i_ems_admin_token") || "";
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${apiPath.baseURL}${apiPath.profilePicture}`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const updatedProfile = res?.data?.data;
      if (updatedProfile) {
        setUserProfile(updatedProfile);
        localStorage.setItem("i_ems_admin_profile", JSON.stringify(updatedProfile));
      }
      message.success("Profile picture updated");
    } catch (err) {
      setPictureUrl(previousUrl);
      message.error(err?.response?.data?.message || "Could not update profile picture");
    } finally {
      setUploading(false);
    }
  };

  const onRemove = async () => {
    setUploading(true);
    try {
      const token = localStorage.getItem("i_ems_admin_token") || "";
      const res = await axios.delete(`${apiPath.baseURL}${apiPath.profilePicture}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedProfile = res?.data?.data;
      if (updatedProfile) {
        setUserProfile(updatedProfile);
        localStorage.setItem("i_ems_admin_profile", JSON.stringify(updatedProfile));
      }
      setPictureUrl(null);
      message.success("Profile picture removed");
    } catch (err) {
      message.error(err?.response?.data?.message || "Could not remove profile picture");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div
        className="i-ems-avatar-trigger"
        onClick={(e) => {
          // Stop this click from also bubbling up to the parent Dropdown
          // trigger (the avatar sits inside the user-menu dropdown's
          // trigger area) — done here rather than via an extra wrapping
          // <span> in Main.js, since that inline span caused a several-px
          // baseline-alignment offset versus its flex sibling (the name
          // text), making the avatar look misaligned/floating high.
          e.stopPropagation();
          setModalOpen(true);
        }}
      >
        <Avatar src={pictureUrl} icon={!pictureUrl ? <UserOutlined /> : undefined} />
      </div>

      <Modal
        title="Profile Picture"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
        centered
      >
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onFileSelected} />
        <div className="i-ems-avatar-modal-preview">
          {uploading ? (
            <Spin size="large" />
          ) : (
            <Avatar size={180} src={pictureUrl} icon={!pictureUrl ? <UserOutlined style={{ fontSize: 80 }} /> : undefined} />
          )}
        </div>
        <Space style={{ width: "100%", justifyContent: "center", marginTop: 20 }}>
          <Button type="primary" icon={<CameraOutlined />} loading={uploading} onClick={() => fileInputRef.current?.click()}>
            {pictureUrl ? "Change Photo" : "Upload Photo"}
          </Button>
          {pictureUrl && (
            <Popconfirm title="Remove profile picture?" onConfirm={onRemove}>
              <Button danger icon={<DeleteOutlined />} disabled={uploading}>
                Remove
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Modal>
    </>
  );
};

export default ProfileAvatar;
